// page-layout.service.ts
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * PageLayoutService
 * -----------------
 * RESPONSIBILITY:
 * - Owns ALL height calculations for ONE page
 * - Tracks:
 *   - viewport height
 *   - navbar height
 *   - registered static section heights
 * - Calculates remaining height for dynamic sections
 *
 * VERY IMPORTANT:
 * - One instance PER PAGE (provided in component providers[])
 * - This service is the SINGLE SOURCE OF TRUTH for layout math
 */
@Injectable()
export class PageLayoutService {

  private contexts = new Map<string,
  {
    staticSections: Map<string, number>;
    staticTotal: number;
    staticReady$: BehaviorSubject<boolean>;
  }
  >();

  private staticChange$ = new BehaviorSubject<void>(undefined);

  /** Height of the global navbar/header */
  public navbarHeight$ = new BehaviorSubject<number>(0);

  /**
   * Observable holding current viewport height.
   * Used to trigger recalculation on resize.
   */
  private viewportHeight$ = new BehaviorSubject<number>(window.innerHeight);

  constructor(private zone: NgZone) {

    /**
     * Listen to window resize OUTSIDE Angular
     * - prevents unnecessary change detection
     * - debounced to avoid layout thrashing
     */
    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => {
          const h = window.innerHeight;
          this.viewportHeight$.next(h);
        });
    });
  }

  /* ================= NAVBAR ================= */

  /**
   * Called ONCE from the layout component.
   * Navbar height is global and does NOT change per page.
   */
  setNavbarHeight(height: number): void {
    if (height !== this.navbarHeight$.value) {
      this.navbarHeight$.next(height);
    }
  }

  /* ================= STATIC SECTIONS ================= */

  /**
   * Registers a static section.
   * Static sections CONTRIBUTE to height subtraction.
   *
   * @param id     Unique identifier for the section
   * @param height Measured pixel height of the section
   */
   registerStatic(contextKey: string, id: string, height: number): void {
    
    const ctx = this.getContext(contextKey);
  
    ctx.staticSections.set(id, height);
    ctx.staticTotal = Array.from(ctx.staticSections.values())
      .reduce((s, h) => s + h, 0);
  
    
    queueMicrotask(() => {
      ctx.staticReady$.next(true);
    });
  
  }
  
  

  /**
   * Unregisters static section when destroyed.
   * Prevents stale heights when navigating away.
   */
   unregisterStatic(contextKey: string, id: string): void {
    const ctx = this.getContext(contextKey);
  
    ctx.staticSections.delete(id);
    ctx.staticTotal = Array.from(ctx.staticSections.values())
      .reduce((s, h) => s + h, 0);
  
    queueMicrotask(() => {
      ctx.staticReady$.next(true);
    });
  
  }
  
  

  /* ================= DYNAMIC HEIGHT CALCULATION ================= */

  /**
   * Calculates pixel height for a dynamic section.
   *
   * IMPORTANT CONCEPT:
   * - Static sections DECLARE facts (their height)
   * - Dynamic sections APPLY policy (how much space to take)
   *
   * @param allocatePercent Percentage of remaining height (default 100)
   * @param extraOffset     Manual subtraction (margins, safety gap, etc.)
   */
   calculateDynamicHeight(
    contextKey: string,
    allocatePercent = 100,
    extraOffset = 0
  ): number {
  
    const ctx = this.getContext(contextKey);
    const viewport = this.viewportHeight$.value;
    const currentNavHeight = this.navbarHeight$.value;
  
    const remaining =
      viewport -
      currentNavHeight -
      ctx.staticTotal -
      extraOffset;
  
    const finalHeight =
      remaining > 0
        ? Math.floor(remaining * (allocatePercent / 100))
        : 0;
  
    
      // console.log("GG : ", contextKey, allocatePercent, "eoff ::" , extraOffset , "static ::" , ctx.staticTotal , "viewport ::" , viewport, "final ::" , finalHeight, "remaining ::" , remaining);

    return finalHeight;
  }
  

  /* ================= RESIZE SUBSCRIPTION ================= */

  /**
   * Allows dynamic sections to reapply height on resize.
   * Centralized and debounced → safe and cheap.
   */
  onResize(callback: () => void) {
    return this.viewportHeight$.subscribe(() => callback());
  }

  private getContext(contextKey: string) {
    if (!this.contexts.has(contextKey)) {
      this.contexts.set(contextKey, {
        staticSections: new Map(),
        staticTotal: 0,
        staticReady$: new BehaviorSubject<boolean>(false)
      });
    }
    // console.log("GG contexts : " , this.contexts);
    
    return this.contexts.get(contextKey)!;
  }

  onStaticReady(contextKey: string) {
    return this.getContext(contextKey).staticReady$.asObservable();
  }
  

}
